import { AfterViewChecked, ChangeDetectorRef, Component, ElementRef, EventEmitter, HostBinding, Input, OnDestroy, OnInit, Output, ViewChild, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ConfigService } from 'src/app/services/config.service';
import { IdentityService } from 'src/app/services/identity.service';
import { HostsService } from 'src/app/services/hosts.service';
import { Host } from 'src/app/models/host';
import { HostGpuStatsComponent } from '../devices/host-gpu-stats/host-gpu-stats.component';
import { LitTheme, ChartTheme, SettingsService, ColorPreset, COLOR_PRESETS, ThemeColors } from 'src/app/services/settings.service';
import { TrustService } from 'src/app/services/trust.service';
import { SocketService } from 'src/app/services/socket.service';
import { ClaudeAuthWizardService, AuthStatus } from 'src/app/services/claude-auth-wizard.service';
import { AntigravityAuthWizardService, AntigravityAuthStatus } from 'src/app/services/antigravity-auth-wizard.service';
import { CodexAuthWizardService, CodexAuthStatus } from 'src/app/services/codex-auth-wizard.service';
import { MuxAgentService } from 'src/app/services/mux-agent.service';
import { MuxSessionService } from 'src/app/services/mux-session.service';
import { ExternalAppService, ExternalApp } from 'src/app/services/external-app.service';
import { KeycloakService } from 'keycloak-angular';
import { UserPrefsService } from 'src/app/services/user-prefs.service';
import { TeamsService, TeamInfo } from 'src/app/services/teams.service';
import { WidgetService } from 'src/app/services/widget.service';
import { Subscription, lastValueFrom } from 'rxjs';
import { DialogService } from '../../services/dialog.service';

interface BackendStatus {
  backend_id: string;
  auth_status: 'authenticated' | 'not_authenticated' | 'error' | 'installed' | 'not_installed';
  error?: string;
}

interface GeminiStatus {
  backend_id: string;
  configured: boolean;
  healthy: boolean;
  error?: string;
}

type AIBackend = 'claude-cli' | 'gemini' | 'chatgpt' | 'antigravity';

export type FullTheme = { bg: string; mode: LitTheme; chart: ChartTheme };

@Component({
    selector: 'app-settings',
    templateUrl: './settings.component.html',
    styleUrls: ['./settings.component.scss'],
    standalone: false
})
export class SettingsComponent implements OnInit, OnDestroy, AfterViewChecked {
  private dialogs = inject(DialogService);
  private subs: Subscription[] = [];
  @ViewChild('customBackgroundInput') customBackgroundInput: ElementRef<HTMLInputElement>;
  @ViewChild('gpuStats') gpuStats: HostGpuStatsComponent;
  @ViewChild('gwContainer') gwContainer: ElementRef<HTMLDivElement>;
  private _gwMounted = false;

  @HostBinding('class.widget')
  @Input()
  isWidget: boolean = false;

  @Input() initialTab: 'setup' | 'system' | 'theme' | 'display' | 'gpus' | 'labs' | 'services' | 'audio' | 'teams' | null = null;

  @Output() onStartChat = new EventEmitter<void>();

  tab: 'setup' | 'system' | 'theme' | 'display' | 'gpus' | 'labs' | 'services' | 'audio' | 'teams' | 'mobile' | 'computers' | 'models' = 'setup';
  gpusLoading: boolean = false;
  tabs = [
    { icon: 'rocket_launch', name: 'setup', title: 'Setup' },
    // Teams tab: LOCAL MODE ONLY. On SSO nodes Keycloak is the source of
    // truth for team existence/membership (docs/plans/org-policy.md,
    // 2026-09-01) — a local CRUD surface there is a second write path that
    // drifts. Team deletion in Keycloak = orphaning, never data deletion.
    ...(ConfigService.localMode ? [{ icon: 'group_work', name: 'teams' as const, title: 'Teams' }] : []),
    { icon: 'link', name: 'services', title: 'Connected Services' },
    { icon: 'smartphone', name: 'mobile', title: 'Mobile' },
    { icon: 'computer', name: 'computers', title: 'Computers' },
    { icon: 'computer', name: 'system', title: 'System' },
    { icon: 'palette', name: 'theme', title: 'Theme' },
    { icon: 'tune', name: 'display', title: 'Display' },
    { icon: 'mic', name: 'audio', title: 'Audio' },
    { icon: 'developer_board', name: 'gpus', title: 'GPUs' },
    { icon: 'science', name: 'labs', title: 'Labs' }
  ].filter(tab => tab.name !== 'services' || ConfigService.connectedServicesEnabled);

  // Mobile tab (device-link pairing)
  deviceLinkQr: SafeHtml | null = null;
  deviceLinkUrl = '';
  deviceLinkToken = '';
  deviceLinkLoading = false;
  deviceLinkError: string | null = null;

  generateDeviceLink(): void {
    this.deviceLinkLoading = true;
    this.deviceLinkError = null;
    this.http.post<{ url: string; token: string; qr_svg: string | null }>(
      `${ConfigService.muxUrl}/device-link`, {})
      .subscribe({
        next: (p) => {
          this.deviceLinkUrl = p.url;
          this.deviceLinkToken = p.token;
          // Strip the SVG's fixed intrinsic size so it scales to the card
          // instead of overflowing it (SvgPathImage emits mm dimensions).
          const svg = p.qr_svg
            ? p.qr_svg.replace(
                /<svg /,
                '<svg style="width:100%;height:auto;display:block" ')
              .replace(/ (width|height)="[^"]*"/g, '')
            : null;
          this.deviceLinkQr = svg
            ? this.sanitizer.bypassSecurityTrustHtml(svg)
            : null;
          this.deviceLinkLoading = false;
          this.cdr.markForCheck();
        },
        error: (err) => {
          this.deviceLinkError = err?.error?.detail || 'Could not create pairing code.';
          this.deviceLinkLoading = false;
          this.cdr.markForCheck();
        },
      });
  }

  // Computers tab (host affinity: link a computer the home can run turns on)
  linkedHosts: { name: string; address: string; port: number; location: string; alive: boolean; linked: boolean; linked_at?: string }[] = [];
  linkedHome = '';
  hostsLoading = false;
  linkToken = '';
  linkUrl = '';
  linkLoading = false;
  linkError: string | null = null;

  loadHosts(): void {
    this.hostsLoading = true;
    this.http.get<{ home: string; hosts: any[] }>(`${ConfigService.muxUrl}/hosts`).subscribe({
      next: (r) => { this.linkedHosts = r.hosts || []; this.linkedHome = r.home || ''; this.hostsLoading = false; this.cdr.markForCheck(); },
      error: () => { this.hostsLoading = false; this.cdr.markForCheck(); },
    });
  }

  /** Mint a device token for a computer: the same credential a phone gets, used once by `lit link`. */
  generateLinkToken(): void {
    this.linkLoading = true; this.linkError = null;
    this.http.post<{ url: string; token: string }>(`${ConfigService.muxUrl}/device-link`, { name: 'computer' }).subscribe({
      next: (p) => { this.linkToken = p.token; this.linkUrl = (p.url || '').replace(/\/mux\/?$/, ''); this.linkLoading = false; this.cdr.markForCheck(); },
      error: (err) => { this.linkError = err?.error?.detail || 'Could not create a link token.'; this.linkLoading = false; this.cdr.markForCheck(); },
    });
  }

  get linkCommand(): string {
    return this.linkToken ? `lit link ${this.linkUrl} ${this.linkToken}` : '';
  }

  copyLinkCommand(): void {
    if (!this.linkCommand) return;
    navigator.clipboard?.writeText(this.linkCommand);
  }

  /** This home's address, for the desktop app's one-field add-connection screen
   *  (it fetches /mux/connect-info from it for the name and sign-in details). */
  get connectUrl(): string {
    return window.location.origin;
  }

  copyConnectUrl(): void {
    navigator.clipboard?.writeText(this.connectUrl).then(
      () => this.snackBar.open('Copied — paste it into the desktop app\'s Add connection screen.', '', { duration: 3000 }),
      () => this.snackBar.open(this.connectUrl, 'OK', { duration: 6000 }));
  }

  async removeHost(name: string): Promise<void> {
    const ok = await this.dialogs.confirm(`Remove ${name}? Channels with affinity for it will run at the home until you link it again. On the computer, run \`lit unlink\` to clear its side.`, { title: 'Remove computer' });
    if (!ok) return;
    this.http.delete(`${ConfigService.muxUrl}/hosts/${encodeURIComponent(name)}`).subscribe({
      next: () => this.loadHosts(),
      error: (err) => { this.linkError = err?.error?.detail || 'Could not remove it.'; this.cdr.markForCheck(); },
    });
  }

  // Connected Services tab
  googleWorkspaceConnected = false;
  googleWorkspaceChecking = true;
  googleWorkspaceApp: ExternalApp | null = null;
  googleWorkspaceLoading = false;
  googleWorkspaceError: string | null = null;
  private _gwAppElement: HTMLElement | null = null;
  private _gwHostId: string | null = null;

  // System tab
  host: Host | null = null;
  isTrusted: boolean = false;
  initializing: boolean = false;
  trustOutput: string = '';

  // Setup tab
  setupLoading: boolean = false;
  claudeStatus: BackendStatus | null = null;
  geminiStatus: GeminiStatus | null = null;
  chatgptStatus: BackendStatus | null = null;
  isClaudeAuthenticated: boolean = false;
  isGeminiConfigured: boolean = false;
  isChatGPTAuthenticated: boolean = false;
  isAntigravityAuthenticated: boolean = false;
  antigravityStatus: BackendStatus | null = null;
  isClaudeInstalled: boolean = true;  // Assume installed until proven otherwise
  isChatGPTInstalled: boolean = true;  // Assume installed until proven otherwise
  hasAgents: boolean = false;
  agentCount: number = 0;

  // Backend selection
  selectedBackend: AIBackend | null = null;

  // Claude auth wizard state
  isAuthenticating: boolean = false;
  authWizardStatus: AuthStatus | null = null;
  authWizardError: string | null = null;
  authCode: string = '';
  isSubmittingCode: boolean = false;
  isCreatingAgent: boolean = false;

  // Gemini configuration state
  geminiApiKey: string = '';
  isConfiguringGemini: boolean = false;
  geminiError: string | null = null;

  // ChatGPT/Codex auth wizard state
  isChatGPTAuthenticating: boolean = false;
  codexAuthStatus: CodexAuthStatus | null = null;
  codexAuthError: string | null = null;
  codexDeviceUrl: string | null = null;
  codexDeviceCode: string | null = null;

  // Antigravity auth wizard state (Google OAuth paste-code, like Claude)
  isAntigravityAuthenticating: boolean = false;
  antigravityAuthStatus: AntigravityAuthStatus | null = null;
  antigravityAuthError: string | null = null;
  antigravityAuthUrl: string | null = null;
  antigravityAuthCode: string = '';
  isSubmittingAntigravityCode: boolean = false;

  // Model selection state
  claudeModels: string[] = [];
  geminiModels: string[] = [];
  chatgptModels: string[] = [];
  antigravityModels: string[] = [];
  selectedClaudeModel: string = '';
  selectedGeminiModel: string = '';
  selectedChatGPTModel: string = '';
  selectedAntigravityModel: string = '';

  get saasMode(): boolean {
    return ConfigService.saasMode;
  }

  get localMode(): boolean {
    return ConfigService.localMode;
  }

  get connectedServicesEnabled(): boolean {
    return ConfigService.connectedServicesEnabled;
  }

  isBackendAllowed(backend: string): boolean {
    const allowed = ConfigService.allowedBackends;
    return allowed === null || allowed.includes(backend);
  }

  /**
   * Effective trust status - always true in local mode since SSH isn't used
   */
  get effectivelyTrusted(): boolean {
    return this.localMode || this.isTrusted;
  }

  /**
   * Whether any AI backend is configured (Claude OR Gemini OR ChatGPT)
   */
  get hasConfiguredBackend(): boolean {
    return this.isClaudeAuthenticated || this.isGeminiConfigured || this.isChatGPTAuthenticated || this.isAntigravityAuthenticated;
  }

  // Theme presets matching lit-mux
  presets: { name: string; id: ColorPreset }[] = [
    { name: 'Default', id: 'default' },
    { name: 'Solarized', id: 'solarized' },
    { name: 'Dracula', id: 'dracula' },
    { name: 'Nord', id: 'nord' },
    { name: 'Gruvbox', id: 'gruvbox' },
    { name: 'One Dark', id: 'onedark' },
    { name: 'GitHub', id: 'github' },
    { name: 'Catppuccin', id: 'catppuccin' },
    { name: 'Tokyo Night', id: 'tokyonight' },
    { name: 'Terminal', id: 'terminal' }
  ];

  /** A white-label deployment's own palette shows as the first tile and is what "no choice" means. */
  get brandPreset(): { name: string; id: 'brand' } | null {
    const n = this.settings.brandName;
    return n ? { name: `${n} (default)`, id: 'brand' } : null;
  }
  get brandActive(): boolean { return !!this.brandPreset && !this.settings.colorPreset && !this.settings.customColors; }

  getPresetPreviewColor(presetId: ColorPreset | 'brand'): string {
    if (presetId === 'brand') return this.settings.brandColors()?.primary ?? '#2aa198';
    const themeMode = this.settings.theme === 'dark' ? 'dark' : 'light';
    return COLOR_PRESETS[themeMode][presetId]?.primary ?? '#2aa198';
  }

  applyPreset(presetId: ColorPreset | 'brand'): void {
    if (presetId === 'brand') { this.clearPreset(); return; }
    this.settings.colorPreset = presetId;
  }

  clearPreset(): void {
    this.settings.colorPreset = null;
    this.settings.customColors = null;
  }

  get activeColors(): ThemeColors {
    return this.settings.getActiveColors();
  }

  setCustomColor(key: keyof ThemeColors, value: string): void {
    // Validate hex color format
    if (/^#[0-9A-Fa-f]{6}$/.test(value)) {
      this.settings.setCustomColor(key, value);
    }
  }

  // Audio tab
  audioDevices: MediaDeviceInfo[] = [];
  selectedMicId: string = '';
  micTestActive = false;
  micTestTranscript = '';
  micTestError = '';
  micTestDiagnostics = '';
  micTestLevel = 0;
  private micTestRecognition: any = null;
  private micTestStream: MediaStream | null = null;
  private micTestAnalyser: AnalyserNode | null = null;
  private micTestAnimFrame: number | null = null;

  // Teams tab
  teams: TeamInfo[] = [];
  teamsLoading = false;
  newTeamName = '';
  editingTeamId: string | null = null;
  editingTeamName = '';
  teamSaving = false;
  teamError = '';

  backgroundType: 'image' | 'color';
  editingSlotIndex: number | null = null;
  fillColors = [
    '#ff8c00',
    '#e81123',
    '#d13438',
    '#c30052',
    '#bf0077',
    '#9a0089',
    '#881798',
    '#744da9',
    '#10893e',
    '#107c10',
    '#018574',
    '#2d7d9a',
    '#0063b1',
    '#6b69d6',
    '#8e8cd8',
    '#8764b8',
    '#038387',
    '#486860',
    '#525e54',
    '#7e735f',
    '#4c4a48',
    '#515c6b',
    '#4a5459',
    '#000000'
  ];

  public get username() {
    return this.identity.me.username;
  }

  public get teamName() {
    return this.identity.me.selectedTeamName ?? 'none';
  }

  /** Back-link from the old home of the credential/agent/model lenses to the
   *  team-scoped Agents app (moved 2026-09-01) — one link per team. */
  private _orgTeams: string[] = [];
  get agentsAppTeams(): string[] {
    // Same source as the palette's team rows (org registry via TeamsService) —
    // two team lists one tab apart must never disagree (2026-09-01).
    return this._orgTeams.length ? this._orgTeams
      : (this.identity.me?.selectedTeamName ? [this.identity.me.selectedTeamName] : []);
  }
  get keycloakAdminUrl(): string {
    return `${ConfigService.keycloakUrl}/admin/master/console/#/${ConfigService.keycloakRealm}/roles`;
  }
  openAgentsApp(team: string): void {
    this.widgetSvc.openTeamAppFor$.next({ name: 'AgentsWidget', team, title: 'Agents' });
  }

  constructor(
    public identity: IdentityService,
    public settings: SettingsService,
    public config: ConfigService,
    private hostsService: HostsService,
    private cdr: ChangeDetectorRef,
    private trustService: TrustService,
    private socketService: SocketService,
    private http: HttpClient,
    private sanitizer: DomSanitizer,
    private authWizard: ClaudeAuthWizardService,
    private codexAuthWizard: CodexAuthWizardService,
    private antigravityAuthWizard: AntigravityAuthWizardService,
    private agentService: MuxAgentService,
    private sessionService: MuxSessionService,
    private externalAppService: ExternalAppService,
    private keycloak: KeycloakService,
    private userPrefs: UserPrefsService,
    private teamsService: TeamsService,
    private widgetSvc: WidgetService,
    private snackBar: MatSnackBar
  ) {
    this.backgroundType = this.settings.backgroundType;
  }

  /** "Restart All Sessions" (2026-09-10): end every live CLI session of the user's agents. */
  restartingSessions = false;
  async restartAllSessions(): Promise<void> {
    if (!await this.dialogs.confirm('Ends every running session for your agents. Conversations resume on their next message.')) return;
    this.restartingSessions = true;
    this.cdr.markForCheck();
    try {
      const res = await this.sessionService.restartAllSessions();
      const n = res?.killed ?? 0;
      this.snackBar.open(`Restarted ${n} session${n === 1 ? '' : 's'}`, 'OK', { duration: 5000 });
    } catch (e: any) {
      this.snackBar.open(e?.error?.detail || 'Could not restart sessions — check your connection and try again.', 'OK', { duration: 8000 });
    } finally {
      this.restartingSessions = false;
      this.cdr.markForCheck();
    }
  }

  // Force change detection for slider real-time updates
  onSliderInput(property: 'fontSize' | 'toolbarOpacity' | 'widgetOpacity', value: number) {
    (this.settings as any)[property] = value;
    this.cdr.detectChanges();
  }

  // ── Teams CRUD ──

  async loadTeams() {
    this.teamsLoading = true;
    this.teamError = '';
    this.teamsService.load().subscribe({
      next: (teams) => { this.teams = teams; this.teamsLoading = false; },
      error: (e) => { this.teamError = 'Failed to load teams'; this.teamsLoading = false; }
    });
  }

  createTeam() {
    const name = this.newTeamName.trim();
    if (!name) return;
    this.teamSaving = true;
    this.teamError = '';
    const slug = name.toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
    this.teamsService.save({ name, slug, description: '' }).subscribe({
      next: () => { this.newTeamName = ''; this.teamSaving = false; this.loadTeams(); },
      error: (e) => { this.teamError = e?.error?.detail || 'Failed to create team'; this.teamSaving = false; }
    });
  }

  startEditTeam(team: TeamInfo) {
    this.editingTeamId = team.id;
    this.editingTeamName = team.name;
  }

  cancelEditTeam() {
    this.editingTeamId = null;
    this.editingTeamName = '';
  }

  saveEditTeam(team: TeamInfo) {
    const name = this.editingTeamName.trim();
    if (!name || name === team.name) { this.cancelEditTeam(); return; }
    this.teamSaving = true;
    this.teamsService.update(team.id, { name }).subscribe({
      next: () => { this.cancelEditTeam(); this.teamSaving = false; this.loadTeams(); },
      error: (e) => { this.teamError = e?.error?.detail || 'Failed to rename team'; this.teamSaving = false; }
    });
  }

  deleteTeam(team: TeamInfo) {
    this.teamError = '';
    this.teamsService.delete(team.id);
    this.teams = this.teams.filter(t => t.id !== team.id);
  }

  selectTab(name: typeof this.tab) {
    this.tab = name;
    if (name === 'computers') this.loadHosts();
    localStorage.setItem('settingsTab', name);
    if (name === 'teams' && this.teams.length === 0) {
      this.loadTeams();
    }
    if (name === 'audio' && this.audioDevices.length === 0) {
      this.loadAudioDevices();
    }
    if (name !== 'audio') {
      this.stopMicTest();
    }
  }

  async ngOnInit() {
    this.teamsService.collection$.subscribe((ts: any[]) => { this._orgTeams = (ts || []).map((t: any) => t.slug || t.name).filter(Boolean); });
    this.teamsService.load();
    // Use initialTab if provided, else restore last-used tab, else default to 'setup'
    if (this.initialTab) {
      this.tab = this.initialTab;
    } else {
      const saved = localStorage.getItem('settingsTab') as typeof this.tab | null;
      if (saved && this.tabs.some(t => t.name === saved)) {
        this.tab = saved;
      }
    }

    await this.loadHost();
    this.subscribeToSocketEvents();
    this.subscribeToAuthWizard();
    this.subscribeToCodexAuthWizard();

    // Load setup status
    await this.refreshSetupStatus();

    // Check connected services
    this.checkConnectedServices();

    // Auto-load tab data if starting on a data-dependent tab
    if (this.tab === 'teams') {
      this.loadTeams();
    }
    if (this.tab === 'audio') {
      this.loadAudioDevices();
    }
      if (this.tab === 'computers') this.loadHosts();
  }

  private async checkConnectedServices(): Promise<void> {
    this.googleWorkspaceChecking = true;
    try {
      // Check MCP server status
      const servers = await this.agentService.getMcpServers();
      const gw = servers.find(s => s.name === 'google-workspace');
      this.googleWorkspaceConnected = !!(gw && gw.running);

      // Discover the external app for embedding
      const teamName = this.identity.me?.selectedTeamName || 'local';
      const apps = await this.externalAppService.discoverApps(teamName);
      this.googleWorkspaceApp = apps.find(a =>
        a.id?.includes('google-workspace') || a.manifest?.name?.toLowerCase().includes('google')
      ) || null;
    } catch {
      this.googleWorkspaceConnected = false;
    }
    this.googleWorkspaceChecking = false;
    this.cdr.detectChanges();
  }

  async mountGoogleWorkspace(container: HTMLElement): Promise<void> {
    if (!this.googleWorkspaceApp || this._gwAppElement) return;
    this.googleWorkspaceLoading = true;
    this.googleWorkspaceError = null;
    this.cdr.detectChanges();
    try {
      await this.externalAppService.loadApp(this.googleWorkspaceApp);
      const teamName = this.identity.me?.selectedTeamName || 'local';
      this._gwHostId = `settings-gw-${Date.now()}`;
      const basePath = this.googleWorkspaceApp.manifest.config?.basePath || this.googleWorkspaceApp.basePath;
      const host = this.externalAppService.createHost(this._gwHostId, teamName, basePath);
      this._gwAppElement = document.createElement(this.googleWorkspaceApp.manifest.element);
      (this._gwAppElement as any).litWidgetHost = host;
      const config = this.googleWorkspaceApp.manifest.config || {};
      for (const [key, value] of Object.entries(config)) {
        this._gwAppElement.setAttribute(key, typeof value === 'string' ? value : JSON.stringify(value));
      }
      this._gwAppElement.setAttribute('base-path', basePath);
      container.appendChild(this._gwAppElement);
      this.googleWorkspaceLoading = false;
    } catch (err: any) {
      this.googleWorkspaceError = err.message || 'Failed to load Google Workspace';
      this.googleWorkspaceLoading = false;
    }
    this.cdr.detectChanges();
  }

  private _destroyGoogleWorkspace(): void {
    if (this._gwAppElement?.parentNode) {
      this._gwAppElement.parentNode.removeChild(this._gwAppElement);
    }
    if (this._gwHostId) {
      this.externalAppService.destroyHost(this._gwHostId);
    }
    this._gwAppElement = null;
    this._gwHostId = null;
  }

  ngOnDestroy() {
    this.subs.forEach(sub => sub.unsubscribe());
    this.authWizard.reset();
    this.codexAuthWizard.reset();
    this._destroyGoogleWorkspace();
    this.stopMicTest();
  }

  ngAfterViewChecked(): void {
    // Mount GW app when the container appears (user switches to services tab)
    if (this.tab === 'services' && this.gwContainer && !this._gwMounted && this.googleWorkspaceApp) {
      this._gwMounted = true;
      this.mountGoogleWorkspace(this.gwContainer.nativeElement);
    }
    // Clean up if user leaves services tab
    if (this.tab !== 'services' && this._gwMounted) {
      this._destroyGoogleWorkspace();
      this._gwMounted = false;
    }
  }

  private subscribeToSocketEvents() {
    this.subs.push(
      this.socketService.initPipEvents.subscribe((response: any) => {
        switch (response.status) {
          case 'INIT':
            this.trustOutput = response.msg || 'Initializing...';
            break;
          case 'COMPLETE':
            this.initializing = false;
            this.isTrusted = true;
            this.trustOutput = 'Trust established successfully';
            // Refresh setup status after trust established
            this.refreshSetupStatus();
            break;
          case 'ERROR':
            this.initializing = false;
            this.trustOutput = response.msg || 'Error establishing trust';
            break;
        }
        this.cdr.detectChanges();
      })
    );
  }

  private subscribeToAuthWizard() {
    this.subs.push(
      this.authWizard.authStatus$.subscribe(async status => {
        this.authWizardStatus = status;

        if (status === 'authenticated') {
          this.isAuthenticating = false;
          await this.checkClaudeStatus();
          this.cdr.detectChanges();
        } else if (status === 'failed' || status === 'cancelled') {
          this.isAuthenticating = false;
          this.cdr.detectChanges();
        }
      })
    );

    this.subs.push(
      this.authWizard.error$.subscribe(error => {
        this.authWizardError = error;
        this.cdr.detectChanges();
      })
    );
  }

  private subscribeToCodexAuthWizard() {
    this.subs.push(
      this.codexAuthWizard.authStatus$.subscribe(async status => {
        this.codexAuthStatus = status;

        if (status === 'authenticated') {
          this.isChatGPTAuthenticating = false;
          this.codexDeviceUrl = null;
          this.codexDeviceCode = null;
          await this.checkChatGPTStatus();
          // Auto-create agent for ChatGPT
          await this.createAgentForBackend('chatgpt');
          this.cdr.detectChanges();
        } else if (status === 'failed' || status === 'cancelled') {
          this.isChatGPTAuthenticating = false;
          this.codexDeviceUrl = null;
          this.codexDeviceCode = null;
          this.cdr.detectChanges();
        }
      })
    );

    this.subs.push(
      this.codexAuthWizard.error$.subscribe(error => {
        this.codexAuthError = error;
        this.cdr.detectChanges();
      })
    );
  }

  // ==================== SETUP TAB METHODS ====================

  /**
   * Refresh all setup status indicators
   */
  async refreshSetupStatus(): Promise<void> {
    this.setupLoading = true;
    this.cdr.detectChanges();

    try {
      await Promise.all([
        this.checkClaudeStatus(),
        this.checkGeminiStatus(),
        this.checkChatGPTStatus(),
        this.checkAntigravityStatus(),
        this.checkAgentStatus()
      ]);
    } catch (error) {
      console.error('Error refreshing setup status:', error);
    } finally {
      this.setupLoading = false;
      this.cdr.detectChanges();
    }
  }

  /**
   * Check Claude CLI authentication status
   */
  async checkClaudeStatus(): Promise<void> {
    try {
      const response = await lastValueFrom(
        this.http.get<BackendStatus>(`${ConfigService.muxUrl}/backends/claude-cli/status`)
      );
      this.claudeStatus = response;
      this.isClaudeInstalled = response.auth_status !== 'not_installed';
      this.isClaudeAuthenticated = response.auth_status === 'authenticated';
    } catch (error) {
      console.error('Failed to check Claude status:', error);
      this.isClaudeAuthenticated = false;
    }
  }

  /**
   * Check Gemini API key configuration status
   */
  async checkGeminiStatus(): Promise<void> {
    try {
      const response = await lastValueFrom(
        this.http.get<GeminiStatus>(`${ConfigService.muxUrl}/backends/gemini/status`)
      );
      console.log('Gemini status response:', response);
      this.geminiStatus = response;
      this.isGeminiConfigured = response.configured && response.healthy;
      console.log('isGeminiConfigured:', this.isGeminiConfigured, 'configured:', response.configured, 'healthy:', response.healthy);
    } catch (error) {
      console.error('Failed to check Gemini status:', error);
      this.isGeminiConfigured = false;
    }
  }

  /**
   * Check ChatGPT/Codex CLI authentication status
   */
  async checkChatGPTStatus(): Promise<void> {
    try {
      const response = await lastValueFrom(
        this.http.get<BackendStatus>(`${ConfigService.muxUrl}/backends/chatgpt/status`)
      );
      this.chatgptStatus = response;
      this.isChatGPTInstalled = response.auth_status !== 'not_installed';
      this.isChatGPTAuthenticated = response.auth_status === 'authenticated';
      this.cdr.detectChanges();
    } catch (error) {
      console.error('Failed to check ChatGPT status:', error);
      this.isChatGPTAuthenticated = false;
    }
  }

  /**
   * Check Antigravity (agy) Google OAuth authentication status
   */
  async checkAntigravityStatus(): Promise<void> {
    try {
      const response = await lastValueFrom(
        this.http.get<BackendStatus>(`${ConfigService.muxUrl}/backends/antigravity/status`)
      );
      this.antigravityStatus = response;
      this.isAntigravityAuthenticated = response.auth_status === 'authenticated';
      this.cdr.detectChanges();
    } catch (error) {
      console.error('Failed to check Antigravity status:', error);
      this.isAntigravityAuthenticated = false;
    }
  }

  /**
   * Check if agents exist
   */
  async checkAgentStatus(): Promise<void> {
    try {
      const agents = await this.agentService.loadAgents();
      this.agentCount = agents.length;
      this.hasAgents = agents.length > 0;
    } catch (error) {
      console.error('Failed to check agent status:', error);
      this.hasAgents = false;
      this.agentCount = 0;
    }
  }

  /**
   * Fetch available models for a backend
   */
  async fetchBackendModels(backend: AIBackend): Promise<void> {
    try {
      const backendId = backend;  // AIBackend values match backend ids 1:1
      // Use /backends/info which returns all backends, then filter
      const response = await lastValueFrom(
        this.http.get<{id: string; models: string[]}[]>(`${ConfigService.muxUrl}/backends/info`)
      );
      const backendInfo = response.find(b => b.id === backendId);
      if (backend === 'claude-cli') {
        this.claudeModels = backendInfo?.models || [];
        if (this.claudeModels.length > 0 && !this.selectedClaudeModel) {
          this.selectedClaudeModel = this.claudeModels[0];
        }
      } else if (backend === 'chatgpt') {
        this.chatgptModels = backendInfo?.models || [];
        if (this.chatgptModels.length > 0 && !this.selectedChatGPTModel) {
          this.selectedChatGPTModel = this.chatgptModels[0];
        }
      } else if (backend === 'antigravity') {
        this.antigravityModels = backendInfo?.models || [];
        if (this.antigravityModels.length > 0 && !this.selectedAntigravityModel) {
          this.selectedAntigravityModel = this.antigravityModels[0];
        }
      } else {
        this.geminiModels = backendInfo?.models || [];
        if (this.geminiModels.length > 0 && !this.selectedGeminiModel) {
          this.selectedGeminiModel = this.geminiModels[0];
        }
      }
    } catch (error) {
      console.error(`Failed to fetch ${backend} models:`, error);
    }
  }

  /**
   * Select a backend to configure
   */
  async selectBackend(backend: AIBackend): Promise<void> {
    // Toggle: clicking the same card again collapses it
    if (this.selectedBackend === backend) {
      this.selectedBackend = null;
      this.cdr.detectChanges();
      return;
    }

    this.selectedBackend = backend;
    // Reset any previous error states
    this.authWizardError = null;
    this.geminiError = null;
    this.codexAuthError = null;
    this.antigravityAuthError = null;

    // Refresh status for the selected backend (in case user logged out externally)
    if (backend === 'claude-cli') {
      await this.checkClaudeStatus();
    } else if (backend === 'gemini') {
      await this.checkGeminiStatus();
    } else if (backend === 'chatgpt') {
      await this.checkChatGPTStatus();
    } else if (backend === 'antigravity') {
      await this.checkAntigravityStatus();
    }

    // Fetch available models for this backend
    this.fetchBackendModels(backend);
    this.cdr.detectChanges();
  }

  /**
   * Start Claude authentication wizard
   */
  async startClaudeAuth(): Promise<void> {
    this.selectedBackend = 'claude-cli';
    this.isAuthenticating = true;
    this.authWizardError = null;
    this.cdr.detectChanges();

    try {
      const session = await this.authWizard.startAuth();

      if (session.oauth_url) {
        // Open OAuth URL in new tab
        window.open(session.oauth_url, '_blank');
        // Start polling for completion
        this.authWizard.startPolling();
      } else if (session.status === 'authenticated') {
        // Already authenticated
        this.isAuthenticating = false;
        await this.checkClaudeStatus();
      } else if (session.error) {
        this.authWizardError = session.error;
        this.isAuthenticating = false;
      }
    } catch (error: any) {
      console.error('Failed to start Claude auth:', error);
      this.authWizardError = error?.error?.detail || error?.message || 'Failed to start authentication';
      this.isAuthenticating = false;
    }
    this.cdr.detectChanges();
  }

  /**
   * Start Antigravity (agy) Google OAuth — opens the consent URL and shows a
   * paste box for the code the callback page displays.
   */
  async startAntigravityAuth(): Promise<void> {
    this.selectedBackend = 'antigravity';
    this.isAntigravityAuthenticating = true;
    this.antigravityAuthError = null;
    this.antigravityAuthCode = '';
    this.antigravityAuthUrl = null;
    this.cdr.detectChanges();

    try {
      const session = await this.antigravityAuthWizard.startAuth();
      this.antigravityAuthStatus = session.status;
      if (session.oauth_url) {
        this.antigravityAuthUrl = session.oauth_url;
        window.open(session.oauth_url, '_blank');
      } else if (session.error) {
        this.antigravityAuthError = session.error;
        this.isAntigravityAuthenticating = false;
      }
    } catch (error: any) {
      console.error('Failed to start Antigravity auth:', error);
      this.antigravityAuthError = error?.error?.detail || error?.message || 'Failed to start authentication';
      this.isAntigravityAuthenticating = false;
    }
    this.cdr.detectChanges();
  }

  /**
   * Submit the pasted Google auth code to complete Antigravity authentication.
   */
  async submitAntigravityCode(): Promise<void> {
    if (!this.antigravityAuthCode.trim()) {
      this.antigravityAuthError = 'Please paste the authorization code';
      return;
    }
    this.isSubmittingAntigravityCode = true;
    this.antigravityAuthError = null;
    this.cdr.detectChanges();

    try {
      const result = await this.antigravityAuthWizard.submitCode(this.antigravityAuthCode.trim());
      if (result.success) {
        this.isAntigravityAuthenticating = false;
        this.antigravityAuthCode = '';
        this.antigravityAuthUrl = null;
        await this.checkAntigravityStatus();
      } else {
        this.antigravityAuthError = result.message || result.error || 'Failed to submit code';
      }
    } catch (error: any) {
      console.error('Failed to submit Antigravity code:', error);
      this.antigravityAuthError = error?.error?.detail || error?.message || 'Failed to submit code';
    }
    this.isSubmittingAntigravityCode = false;
    this.cdr.detectChanges();
  }

  /**
   * Cancel an in-progress Antigravity auth flow.
   */
  async cancelAntigravityAuth(): Promise<void> {
    await this.antigravityAuthWizard.cancelAuth();
    this.isAntigravityAuthenticating = false;
    this.antigravityAuthCode = '';
    this.antigravityAuthUrl = null;
    this.antigravityAuthError = null;
    this.cdr.detectChanges();
  }

  /**
   * Force re-authentication for an already-configured backend
   */
  forceReauth(backend: AIBackend): void {
    if (backend === 'claude-cli') {
      this.isClaudeAuthenticated = false;
      this.authCode = '';
      this.isAuthenticating = false;
    } else if (backend === 'gemini') {
      this.isGeminiConfigured = false;
      this.geminiApiKey = '';
    } else if (backend === 'chatgpt') {
      this.isChatGPTAuthenticated = false;
      this.isChatGPTAuthenticating = false;
    } else if (backend === 'antigravity') {
      this.isAntigravityAuthenticated = false;
      this.antigravityAuthCode = '';
      this.isAntigravityAuthenticating = false;
    }
    this.cdr.detectChanges();
  }

  /**
   * Configure Gemini with API key and model
   */
  async configureGemini(): Promise<void> {
    if (!this.geminiApiKey.trim()) {
      return;
    }

    this.isConfiguringGemini = true;
    this.geminiError = null;
    this.cdr.detectChanges();

    try {
      const config: any = { api_key: this.geminiApiKey.trim() };
      // Include selected model if one was chosen
      if (this.selectedGeminiModel) {
        config.model = this.selectedGeminiModel;
      }
      await lastValueFrom(
        this.http.post(`${ConfigService.muxUrl}/backends/gemini/configure`, config)
      );

      // Clear the input and refresh status
      this.geminiApiKey = '';
      await this.checkGeminiStatus();
      // Auto-create agent for Gemini
      await this.createAgentForBackend('gemini');
    } catch (error: any) {
      console.error('Failed to configure Gemini:', error);
      this.geminiError = error?.error?.detail || error?.message || 'Failed to configure Gemini API key';
    } finally {
      this.isConfiguringGemini = false;
      this.cdr.detectChanges();
    }
  }

  /**
   * Open Google AI Studio to get API key
   */
  openGoogleAIStudio(): void {
    window.open('https://aistudio.google.com/apikey', '_blank');
  }

  /**
   * Open Claude CLI installation docs
   */
  openClaudeInstallDocs(): void {
    window.open('https://docs.anthropic.com/en/docs/claude-code/getting-started', '_blank');
  }

  /**
   * Open Codex CLI installation docs
   */
  openCodexInstallDocs(): void {
    window.open('https://github.com/openai/codex', '_blank');
  }

  /**
   * Start ChatGPT/Codex authentication wizard
   */
  async startChatGPTAuth(): Promise<void> {
    this.selectedBackend = 'chatgpt';
    this.isChatGPTAuthenticating = true;
    this.codexAuthError = null;
    this.codexDeviceUrl = null;
    this.codexDeviceCode = null;
    this.cdr.detectChanges();

    try {
      const session = await this.codexAuthWizard.startAuth();

      if (session.device_url && session.device_code) {
        this.codexDeviceUrl = session.device_url;
        this.codexDeviceCode = session.device_code;
        this.codexAuthWizard.startPolling();
      } else if (session.status === 'authenticated') {
        // Already authenticated - update status and create agent
        this.isChatGPTAuthenticating = false;
        await this.checkChatGPTStatus();
        await this.createAgentForBackend('chatgpt');
        this.selectedBackend = null;  // Hide the config panel
        this.cdr.detectChanges();
      } else if (session.error) {
        this.codexAuthError = session.error;
        this.isChatGPTAuthenticating = false;
      }
    } catch (error: any) {
      console.error('Failed to start ChatGPT auth:', error);
      this.codexAuthError = error?.error?.detail || error?.message || 'Failed to start authentication';
      this.isChatGPTAuthenticating = false;
    }
    this.cdr.detectChanges();
  }

  /**
   * Cancel in-progress ChatGPT authentication
   */
  async cancelChatGPTAuth(): Promise<void> {
    await this.codexAuthWizard.cancelAuth();
    this.isChatGPTAuthenticating = false;
    this.codexDeviceUrl = null;
    this.codexDeviceCode = null;
    this.cdr.detectChanges();
  }

  /**
   * Open the device auth URL in a new tab
   */
  openDeviceAuthUrl(): void {
    if (this.codexDeviceUrl) {
      window.open(this.codexDeviceUrl, '_blank');
    }
  }

  /**
   * Copy device code to clipboard
   */
  async copyDeviceCode(): Promise<void> {
    if (this.codexDeviceCode) {
      try {
        await navigator.clipboard.writeText(this.codexDeviceCode);
      } catch (error) {
        console.error('Failed to copy device code:', error);
      }
    }
  }

  /**
   * Get human-readable Codex auth wizard status text
   */
  getCodexAuthStatusText(): string {
    switch (this.codexAuthStatus) {
      case 'starting':
        return 'Starting authentication...';
      case 'waiting_code':
        return 'Getting device code...';
      case 'waiting':
        return 'Enter the code in your browser...';
      case 'authenticated':
        return 'Authenticated successfully!';
      case 'failed':
        return this.codexAuthError || 'Authentication failed';
      case 'cancelled':
        return 'Authentication cancelled';
      case 'needs_intervention':
        return 'Authentication requires manual intervention';
      default:
        return '';
    }
  }

  /**
   * Cancel in-progress authentication
   */
  async cancelClaudeAuth(): Promise<void> {
    await this.authWizard.cancelAuth();
    this.isAuthenticating = false;
    this.authCode = '';
    this.cdr.detectChanges();
  }

  /**
   * Submit the auth code manually
   */
  async submitAuthCode(): Promise<void> {
    if (!this.authCode.trim()) {
      return;
    }

    this.isSubmittingCode = true;
    this.authWizardError = null;
    this.cdr.detectChanges();

    try {
      const result = await this.authWizard.submitCode(this.authCode.trim());

      if (result.success) {
        this.isAuthenticating = false;
        this.authCode = '';
        // Save model preference along with auth
        if (this.selectedClaudeModel) {
          await this.saveClaudeModel();
        }
        await this.checkClaudeStatus();
        // Auto-create agent for Claude
        await this.createAgentForBackend('claude-cli');
      } else {
        this.authWizardError = result.message || 'Failed to submit code';
      }
    } catch (error: any) {
      console.error('Failed to submit auth code:', error);
      this.authWizardError = error?.error?.detail || error?.message || 'Failed to submit code';
    } finally {
      this.isSubmittingCode = false;
      this.cdr.detectChanges();
    }
  }

  /**
   * Save Claude model preference (after authentication)
   */
  async saveClaudeModel(): Promise<void> {
    if (!this.selectedClaudeModel) return;

    try {
      await lastValueFrom(
        this.http.post(`${ConfigService.muxUrl}/backends/claude-cli/configure`, {
          model: this.selectedClaudeModel
        })
      );
    } catch (error: any) {
      console.error('Failed to save Claude model:', error);
      this.authWizardError = error?.error?.detail || 'Failed to save model preference';
    }
    this.cdr.detectChanges();
  }

  /**
   * Create an agent for a specific backend if one doesn't already exist
   */
  async createAgentForBackend(backendId: 'claude-cli' | 'gemini' | 'chatgpt' | 'antigravity'): Promise<void> {
    this.isCreatingAgent = true;
    this.cdr.detectChanges();

    try {
      const agents = await this.agentService.loadAgents();

      // Check if an agent for this backend already exists
      const existingAgent = agents.find((a: any) => a.backend === backendId);
      if (existingAgent) {
        // Already have an agent for this backend, just update UI
        this.hasAgents = true;
        this.agentCount = agents.length;
        return;
      }

      // Create agent for the new backend
      let name: string;
      let model: string;
      if (backendId === 'gemini') {
        name = 'Gemini';
        model = this.selectedGeminiModel || 'gemini-2.5-flash';
      } else if (backendId === 'antigravity') {
        name = 'Antigravity';
        model = this.selectedAntigravityModel || 'Gemini 3.8 Flash (High)';
      } else if (backendId === 'chatgpt') {
        name = 'ChatGPT';
        // Default to gpt-6.1-sol for ChatGPT accounts (OpenAI's own Codex default; gpt-5.x retired from Codex)
        model = this.selectedChatGPTModel || 'gpt-6.1-sol';
      } else {
        name = 'Claude';
        model = this.selectedClaudeModel || 'claude-sonnet-4-20250514';
      }

      // Use backend name as ID if this is the first agent, otherwise use backend-specific ID
      const id = agents.length === 0 ? 'default' : backendId;

      await this.agentService.createAgent({
        id,
        name,
        backend: backendId,
        model,
        system_prompt: '',
        temperature: 1.0,
        heartbeat_enabled: true,
        heartbeat_interval: 300,
        mcp_servers: [],
        metadata: {}
      });

      // Set as current agent if it's the first one
      if (agents.length === 0) {
        this.agentService.setCurrentAgent(id);
      }

      this.hasAgents = true;
      this.agentCount = agents.length + 1;
    } catch (error: any) {
      console.error('Failed to create agent:', error);
    } finally {
      this.isCreatingAgent = false;
      this.cdr.detectChanges();
    }
  }

  /**
   * Get human-readable auth wizard status text
   */
  getAuthWizardStatusText(): string {
    switch (this.authWizardStatus) {
      case 'starting':
        return 'Starting authentication...';
      case 'waiting_url':
        return 'Waiting for OAuth URL...';
      case 'waiting':
        return 'Complete sign-in in your browser...';
      case 'submitting':
        return 'Submitting authentication code...';
      case 'authenticated':
        return 'Authenticated successfully!';
      case 'failed':
        return this.authWizardError || 'Authentication failed';
      case 'cancelled':
        return 'Authentication cancelled';
      default:
        return '';
    }
  }

  /**
   * Check if all setup steps are complete
   */
  get isSetupComplete(): boolean {
    return this.effectivelyTrusted && this.hasConfiguredBackend && this.hasAgents;
  }

  startChatting(): void {
    localStorage.setItem('lit-setup-completed', 'true');
    this.onStartChat.emit();
  }

  // ==================== AUDIO TAB METHODS ====================

  async loadAudioDevices(): Promise<void> {
    try {
      // Request permission first so labels are populated
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach(t => t.stop());
      const devices = await navigator.mediaDevices.enumerateDevices();
      this.audioDevices = devices.filter(d => d.kind === 'audioinput');
      this.selectedMicId = this.settings.microphoneDeviceId || '';
      this.cdr.detectChanges();
    } catch (err) {
      console.error('Failed to enumerate audio devices:', err);
    }
  }

  onMicSelect(deviceId: string): void {
    this.selectedMicId = deviceId;
    this.settings.microphoneDeviceId = deviceId;
    this.stopMicTest();
  }

  async startMicTest(): Promise<void> {
    this.stopMicTest();
    this.micTestActive = true;
    this.micTestTranscript = '';
    this.micTestError = '';
    this.micTestDiagnostics = '';
    this.cdr.detectChanges();

    // Start audio level meter
    try {
      const constraints: MediaStreamConstraints = {
        audio: this.selectedMicId ? { deviceId: { exact: this.selectedMicId } } : true
      };
      this.micTestStream = await navigator.mediaDevices.getUserMedia(constraints);
      const ctx = new AudioContext();
      const source = ctx.createMediaStreamSource(this.micTestStream);
      this.micTestAnalyser = ctx.createAnalyser();
      this.micTestAnalyser.fftSize = 256;
      source.connect(this.micTestAnalyser);
      this.pollAudioLevel();
    } catch (err: any) {
      this.micTestError = 'Microphone access denied';
      this.micTestActive = false;
      this.cdr.detectChanges();
      return;
    }

    // Start speech recognition
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) {
      this.micTestError = 'Speech recognition not supported in this browser';
      this.micTestActive = false;
      this.cdr.detectChanges();
      return;
    }

    this.micTestRecognition = new SpeechRecognition();
    this.micTestRecognition.continuous = true;
    this.micTestRecognition.interimResults = true;
    this.micTestRecognition.lang = 'en-US';

    this.micTestRecognition.onresult = (event: any) => {
      let transcript = '';
      for (let i = 0; i < event.results.length; i++) {
        transcript += event.results[i][0].transcript;
      }
      this.micTestTranscript = transcript;
      this.cdr.detectChanges();
    };

    this.micTestRecognition.onerror = (event: any) => {
      if (event.error !== 'no-speech') {
        const isBrave = !!(navigator as any).brave;
        if (event.error === 'network' && isBrave) {
          this.micTestError = 'Brave Browser blocks speech recognition — see details below';
        } else {
          this.micTestError = `Speech recognition error: ${event.error}`;
        }
        this.micTestDiagnostics = this._buildSpeechDiagnostics(event);
        this.cdr.detectChanges();
      }
    };

    this.micTestRecognition.onend = () => {
      if (this.micTestActive) {
        try { this.micTestRecognition?.start(); } catch {}
      }
    };

    try {
      this.micTestRecognition.start();
    } catch (err: any) {
      this.micTestError = `Failed to start: ${err.message}`;
      this.micTestActive = false;
      this.cdr.detectChanges();
    }
  }

  stopMicTest(): void {
    this.micTestActive = false;
    if (this.micTestRecognition) {
      this.micTestRecognition.onend = null;
      this.micTestRecognition.stop();
      this.micTestRecognition = null;
    }
    if (this.micTestStream) {
      this.micTestStream.getTracks().forEach(t => t.stop());
      this.micTestStream = null;
    }
    if (this.micTestAnimFrame) {
      cancelAnimationFrame(this.micTestAnimFrame);
      this.micTestAnimFrame = null;
    }
    this.micTestAnalyser = null;
    this.micTestLevel = 0;
    this.cdr.detectChanges();
  }

  private _buildSpeechDiagnostics(event: any): string {
    const lines: string[] = [];
    lines.push(`Error: ${event.error}`);
    if (event.message) lines.push(`Message: ${event.message}`);

    lines.push(`Browser: ${navigator.userAgent}`);
    lines.push(`Platform: ${navigator.platform}`);
    lines.push(`Online: ${navigator.onLine}`);
    lines.push(`Protocol: ${location.protocol}`);
    lines.push(`Host: ${location.host}`);

    const mic = this.audioDevices.find(d => d.deviceId === this.selectedMicId);
    if (mic) {
      lines.push(`Microphone: ${mic.label || mic.deviceId}`);
    } else {
      lines.push(`Microphone: ${this.selectedMicId || '(default)'}`);
    }
    lines.push(`Available mics: ${this.audioDevices.length}`);

    if (this.micTestStream) {
      const track = this.micTestStream.getAudioTracks()[0];
      if (track) {
        lines.push(`Audio track state: ${track.readyState}`);
        const settings = track.getSettings();
        if (settings.sampleRate) lines.push(`Sample rate: ${settings.sampleRate}`);
      }
    }

    const isBrave = !!(navigator as any).brave;

    if (event.error === 'network' && isBrave) {
      lines.push('');
      lines.push('Brave Browser Detected');
      lines.push('Brave blocks the Google speech recognition service by default.');
      lines.push('');
      lines.push('To fix:');
      lines.push('1. Open brave://settings/shields');
      lines.push('2. Turn off "Prevent sites from fingerprinting" for this site');
      lines.push('3. Or: open Brave Shields (lion icon in address bar)');
      lines.push('   → set Fingerprinting to "Allow"');
      lines.push('4. Reload the page and try again');
      lines.push('');
      lines.push('Alternatively, use Chrome or Edge which support speech recognition natively.');
    } else if (event.error === 'network') {
      lines.push('');
      lines.push('Possible causes:');
      lines.push('- Browser privacy shields blocking Google speech services');
      lines.push('- No internet connection or firewall blocking outbound requests');
      lines.push('- Browser does not support Web Speech API (Firefox, some Chromium forks)');
      lines.push('- Self-signed or invalid SSL certificate on this page');
    } else if (event.error === 'not-allowed') {
      lines.push('');
      lines.push('Possible causes:');
      lines.push('- Microphone permission denied in browser settings');
      lines.push('- Page not served over HTTPS');
    } else if (event.error === 'service-not-allowed') {
      lines.push('');
      lines.push('Possible causes:');
      lines.push('- Speech recognition service disabled by browser policy');
      lines.push('- Browser does not support Web Speech API');
    }

    lines.push('');
    lines.push(`Timestamp: ${new Date().toISOString()}`);
    return lines.join('\n');
  }

  copyDiagnostics(): void {
    if (this.micTestDiagnostics) {
      navigator.clipboard.writeText(this.micTestDiagnostics);
      this.micTestError = 'Diagnostics copied to clipboard';
      setTimeout(() => {
        if (this.micTestError === 'Diagnostics copied to clipboard') {
          this.micTestError = '';
          this.micTestDiagnostics = '';
          this.cdr.detectChanges();
        }
      }, 2000);
      this.cdr.detectChanges();
    }
  }

  private pollAudioLevel(): void {
    if (!this.micTestAnalyser || !this.micTestActive) return;
    const data = new Uint8Array(this.micTestAnalyser.frequencyBinCount);
    this.micTestAnalyser.getByteFrequencyData(data);
    const avg = data.reduce((sum, v) => sum + v, 0) / data.length;
    this.micTestLevel = Math.min(100, Math.round(avg / 128 * 100));
    this.cdr.detectChanges();
    this.micTestAnimFrame = requestAnimationFrame(() => this.pollAudioLevel());
  }

  async loadHost() {
    try {
      const hosts = await this.hostsService.loadAsync(this.teamName);
      if (hosts.length > 0) {
        this.host = hosts[0];
        this.isTrusted = this.host.isTrusted ?? false;
      }
    } catch (error) {
      console.error('Failed to load host:', error);
    }
  }

  async toggleTrust() {
    if (!this.host) return;

    if (this.isTrusted) {
      // Remove trust
      this.initializing = true;
      this.trustOutput = 'Removing trust...';
      try {
        await this.trustService.delete(this.teamName, this.host.name).toPromise();
        this.isTrusted = false;
        this.trustOutput = 'Trust removed';
      } catch (error) {
        this.trustOutput = 'Error removing trust';
        console.error('Trust removal error:', error);
      } finally {
        this.initializing = false;
        this.cdr.detectChanges();
      }
    } else {
      // Establish trust - prompt for password
      this.initializing = true;
      this.trustOutput = 'Waiting for password...';
      try {
        const result = await this.trustService.promptUserForCreds(this.teamName, this.host.name, true);
        if (result.canceled) {
          this.trustOutput = '';
        } else if (result.isTrusted) {
          this.isTrusted = true;
          this.trustOutput = 'Trust established successfully';
          await this.refreshSetupStatus();
        } else {
          this.trustOutput = 'Trust could not be established';
        }
      } catch (error) {
        this.trustOutput = 'Error establishing trust';
        console.error('Trust error:', error);
      } finally {
        this.initializing = false;
        this.cdr.detectChanges();
      }
    }
  }

  selectTheme(theme: FullTheme) {
    this.settings.theme = theme.mode;
    this.settings.appBackground = theme.bg;
    this.settings.chartTheme = theme.chart;
  }

  $onSlotClick(index: number) {
    const slot = this.settings.backgroundSlots[index];
    if (slot) {
      // Slot has an image - select it as background
      this.settings.appBackground = slot;
    } else {
      // Empty slot - trigger upload
      this.$uploadToSlot(index);
    }
  }

  async $uploadToSlot(index: number) {
    try {
      const file = await this.selectImageFile();
      if (!file) return;

      const imageUrl = await this.uploadWallpaper(file);
      const bgValue = `url(${imageUrl})`;
      this.settings.setBackgroundSlot(index, bgValue);
      this.settings.appBackground = bgValue;
    } catch (error) {
      if (error instanceof Event && error.type === 'cancel') return;
      console.error('Failed to upload wallpaper:', error);
    }
  }

  $replaceSlot(event: Event, index: number) {
    event.stopPropagation();
    this.$uploadToSlot(index);
  }

  private selectImageFile(): Promise<File | null> {
    return new Promise((resolve, reject) => {
      const input = document.createElement('input');
      Object.assign(input, { type: 'file', accept: 'image/*' });
      input.click();

      input.onchange = () => {
        resolve(input.files?.[0] ?? null);
      };
      input.addEventListener('cancel', () => resolve(null));
      input.onerror = reject;
    });
  }

  private async uploadWallpaper(file: File): Promise<string> {
    const formData = new FormData();
    formData.append('file', file);

    const muxUrl = ConfigService.muxUrl.replace(/\/api$/, '');
    const headers: Record<string, string> = {};
    if (!ConfigService.localMode) {
      try {
        await this.keycloak.updateToken(30);
        const token = await this.keycloak.getToken();
        if (token) headers['Authorization'] = `Bearer ${token}`;
      } catch { }
    }
    const resp = await fetch(`${muxUrl}/api/upload`, {
      method: 'POST',
      headers,
      body: formData
    });

    if (!resp.ok) {
      throw new Error(`Upload failed: ${resp.status} ${resp.statusText}`);
    }

    const data = await resp.json();
    if (!data.success) {
      throw new Error('Upload failed');
    }

    // Match the URL construction from chat-widget
    return `${muxUrl}/uploads/${data.filename}`;
  }

  $clickSelectBackgroundColor() {
    this.customBackgroundInput.nativeElement.click();
  }

  $selectBackgroundColor(color: Event | string) {
    if (color instanceof Event) color = this.customBackgroundInput.nativeElement.value;
    else this.customBackgroundInput.nativeElement.value = color;

    this.settings.appBackground = color as string;
  }

  refreshGpus() {
    if (this.gpuStats) {
      this.gpusLoading = true;
      this.gpuStats.load();
      // Give a brief visual feedback that refresh happened
      setTimeout(() => {
        this.gpusLoading = false;
      }, 500);
    }
  }
}
